import { useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, IconButton, useTheme, useMediaQuery, Typography } from '@mui/material';
import { IconX } from '@tabler/icons';
import NotificationSettings from './sections/Notifications/NotificationSettings';
import { setMockLink } from 'graphql/config';
import { createMockLink } from './sections/Notifications/mocks/mockLink';
import { capitalize } from 'utils/stringHelpers';

interface SettingsDialogProps {
    open: boolean;
    onClose: () => void;
}

const SettingsDialog = ({ open, onClose }: SettingsDialogProps) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const USE_MOCKS = true;

    useEffect(() => {
        if (USE_MOCKS && open) {
            setMockLink(createMockLink());
        }

        return () => {
            if (USE_MOCKS) {
                setMockLink(null);
            }
        };
    }, [USE_MOCKS, open]);

    const handleClose = () => {
        onClose();
    };

    const tenantName = localStorage.getItem('tenantPath')?.replaceAll('/', '') || 'OrderBahn';

    return (
        <Dialog
            data-testid="settings-dialog"
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            fullScreen={fullScreen}
            sx={{
                '& .MuiDialog-paper': {
                    height: fullScreen ? '100%' : '90vh',
                    maxHeight: fullScreen ? '100%' : '90vh'
                }
            }}
        >
            <DialogTitle
                data-testid="settings-dialog-title"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    pb: 2,
                    px: 3
                }}
            >
                <Typography variant="h4" sx={{ fontWeight: 400 }}>
                    Settings -{' '}
                    <Box component="span" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                        {capitalize(tenantName)}
                    </Box>
                </Typography>
                <IconButton data-testid="settings-dialog-close-button" onClick={handleClose} size="small">
                    <IconX />
                </IconButton>
            </DialogTitle>

            <DialogContent
                data-testid="settings-dialog-content"
                sx={{ p: 2, mt: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            >
                <Box sx={{ borderRadius: '8px', border: `1px solid ${theme.palette.divider}`, flex: 1, p: 1 }}>
                    <NotificationSettings />
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default SettingsDialog;
